import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, X } from 'lucide-react';
import { listSiteCategories, createSiteCategory, deleteSiteCategory } from '../../api/sites';
import { apiErrorMessage } from '../../lib/api';
import { useToast } from '../../components/ui/Toast';
import { Modal } from '../../components/ui/Modal';
import { Input } from '../../components/ui/Input';
import { Button } from '../../components/ui/Button';
import { Alert } from '../../components/ui/Alert';
import { Spinner } from '../../components/ui/Spinner';
import type { CustomerSite } from '../../types/api';

// Each site keeps its own independent category list, seeded from the same
// defaults every site gets at creation - editable from here, and only
// affects this one site (§ Perry: "independent per site but by default
// inherits the global ones").
export function SiteCategoriesModal({
  orgId,
  site,
  open,
  onClose,
}: {
  orgId: string;
  site: CustomerSite | null;
  open: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const siteId = site?.id ?? '';

  const categoriesQuery = useQuery({
    queryKey: ['site-categories', siteId],
    queryFn: () => listSiteCategories(orgId, siteId),
    enabled: open && !!siteId,
  });

  const addMutation = useMutation({
    mutationFn: () => createSiteCategory(orgId, siteId, name.trim()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['site-categories', siteId] });
      setName('');
    },
    onError: (err) => setError(apiErrorMessage(err, 'Could not add the category.')),
  });

  const removeMutation = useMutation({
    mutationFn: (categoryId: string) => deleteSiteCategory(orgId, siteId, categoryId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['site-categories', siteId] });
      showToast('Category removed.');
    },
    onError: (err) => setError(apiErrorMessage(err, 'Could not remove the category.')),
  });

  function handleAdd(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!name.trim()) return;
    addMutation.mutate();
  }

  if (!site) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Categories - ${site.name}`}
      footer={
        <Button variant="secondary" type="button" onClick={onClose}>
          Close
        </Button>
      }
    >
      <div className="flex flex-col gap-4">
        <p className="text-sm text-slate-500">
          Offered when adding or editing catalog items at this site. Independent of other sites - removing one here
          doesn't affect them, and doesn't change items already tagged with it.
        </p>
        {error && <Alert tone="error">{error}</Alert>}

        {categoriesQuery.isLoading ? (
          <div className="py-6 text-center">
            <Spinner />
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {(categoriesQuery.data ?? []).map((c) => (
              <span
                key={c.id}
                className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 py-1 pl-3 pr-1.5 text-sm text-slate-700"
              >
                {c.name}
                <button
                  type="button"
                  onClick={() => {
                    setError(null);
                    removeMutation.mutate(c.id);
                  }}
                  className="rounded-full p-0.5 text-slate-400 hover:bg-slate-200 hover:text-red-600"
                  aria-label={`Remove ${c.name}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
            {(categoriesQuery.data ?? []).length === 0 && (
              <span className="text-sm text-slate-400">No categories yet.</span>
            )}
          </div>
        )}

        <form onSubmit={handleAdd} className="flex items-end gap-2">
          <Input
            label="Add category"
            className="w-56"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Monitor"
          />
          <Button type="submit" size="sm" variant="secondary" isLoading={addMutation.isPending}>
            <Plus className="h-3.5 w-3.5" /> Add
          </Button>
        </form>
      </div>
    </Modal>
  );
}
